import I18n from 'I18n';
import { useCallback, useMemo } from 'react';
import AsyncUIIFrame from 'collaboration-sidebar/components/AsyncUIIFrame';
import useWindowDimensions from 'collaboration-sidebar/hooks/useWindowDimensions';
import { IS_OPEN, TAB } from '../../../constants/CollaborationSidebarStateProperties';
import { IFRAME_INIT_TIMEOUT } from '../../../constants/iframe';
import { DEFAULT_WIDTH_CUTOFF } from '../../../constants/sidebar';
import { useCollaborationSidebarState } from '../../../hooks';
import useCollaborationSidebarProps from '../../../hooks/useCollaborationSidebarProps';
import { useIsTabParentRendered } from '../../../hooks/useIsTabParentRendered';
import ParentRenderedContent from '../../../components/ParentRenderedContent';
// @ts-expect-error no-types
import { MSG_TYPE_MODAL_DIALOG_CLOSE } from 'ui-addon-iframeable/messaging/IFrameMessageTypes';
import { getEmbeddablePanelUrl } from 'collaboration-sidebar/utils/urls';
import ErrorMessage from '../../../components/errors/ErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import styled from 'styled-components';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import useHasNavCopilot from 'collaboration-sidebar/features/copilot/hooks/useHasNavCopilot';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUIFlex = styled(UIFlex).withConfig({
  displayName: "CollaborationSidebarContent__StyledUIFlex",
  componentId: "sc-17yfl46-0"
})([".hs-private.hs-private.hs-private &{width:100%;height:100%;}"]);
const LoadingWrapper = styled(UIFlex).withConfig({
  displayName: "CollaborationSidebarContent__LoadingWrapper",
  componentId: "sc-17yfl46-1"
})([".hs-private.hs-private.hs-private &&{width:100%;height:100%;position:absolute;}"]);
export default function CollaborationSidebarContent({
  isOpen = true,
  iframeShouldRender = true
}) {
  const {
    scrollablePage,
    isOverlay,
    objectType,
    enabledFeatures,
    appName,
    ApprovalsContent,
    openLeftEditor,
    embeddedCommentsProps,
    commentableAssets,
    assetName,
    commentableAssetsTitle,
    isListingPage,
    objectId,
    hasUnreadComments,
    hasSidebarPermission,
    iframe: {
      handleSidebarInitError,
      isReady,
      handleReady,
      handleSidebarReadyError,
      IFrameError
    },
    onMessageRef,
    commentingDisabled,
    showSidebarHeader,
    hideCommentModeAndToggle,
    isLoadingFAS,
    iconProps,
    disabledIconProps
  } = useCollaborationSidebarProps();
  const hasNavCopilot = useHasNavCopilot();
  const userInfo = useUserInfo();
  const {
    tab,
    ctaUrl,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();
  const {
    width
  } = useWindowDimensions();
  const isScreenWidthBelowThreshold = useMemo(() => width < DEFAULT_WIDTH_CUTOFF, [width]);
  const handleClose = useCallback(() => {
    onUpdateCollaborationSidebarState({
      [IS_OPEN]: false
    });
    if (openLeftEditor && isScreenWidthBelowThreshold) {
      openLeftEditor();
    }
  }, [isScreenWidthBelowThreshold, onUpdateCollaborationSidebarState, openLeftEditor]);
  const handleOnMessage = useCallback(message => {
    if (message.payload.type === MSG_TYPE_MODAL_DIALOG_CLOSE) {
      handleClose();
    } else if (onMessageRef.current) {
      onMessageRef.current(message);
    }
  }, [handleClose, onMessageRef]);
  const isTabParentRendered = useIsTabParentRendered(tab);
  if (!isLoadingFAS && !hasSidebarPermission) {
    return /*#__PURE__*/_jsx(View, {
      padding: "300",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        illustration: "keys",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'collaborationSidebar.noSidebarPermission.title'
        }),
        message: "collaborationSidebar.noSidebarPermission.message"
      })
    });
  }
  return /*#__PURE__*/_jsxs(StyledUIFlex, {
    children: [/*#__PURE__*/_jsx(ParentRenderedContent, {
      show: isOpen,
      tab: tab,
      enabledFeatures: enabledFeatures,
      ApprovalsContent: ApprovalsContent
    }), isOpen && !isReady && !IFrameError && /*#__PURE__*/_jsx(LoadingWrapper, {
      align: "center",
      justify: "center",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        "data-test-id": "sidebar-content-loading-spinner",
        layout: "centered"
      })
    }), iframeShouldRender && /*#__PURE__*/_jsx(AsyncUIIFrame, {
      embeddedPassthruProps: {
        collaborationSidebarState,
        commentableAssets,
        assetName,
        commentableAssetsTitle,
        isListingPage,
        enabledFeatures,
        hasUnreadComments,
        commentingDisabled,
        appSupportedRoutesRegex: embeddedCommentsProps && embeddedCommentsProps.appSupportedRoutesRegex,
        showSidebarHeader,
        hideCommentModeAndToggle,
        hasNavCopilot,
        userInfo,
        iconProps,
        disabledIconProps
      },
      height: "100%",
      iframePassthruProps: Object.assign({
        'data-test-id': 'collaboration-sidebar-embedded-iframe',
        'data-iframe-ready': isReady,
        title: I18n.text('collaborationSidebar.panel.sidebarTitle'),
        'aria-hidden': !isOpen
      }, !isOpen ? {
        tabIndex: '-1'
      } : {}),
      initTimeout: IFRAME_INIT_TIMEOUT,
      name: "collaboration-sidebar",
      onInitError: handleSidebarInitError,
      onReady: handleReady,
      onReadyError: handleSidebarReadyError,
      scrollablePage: scrollablePage,
      isOverlay: isOverlay,
      src: getEmbeddablePanelUrl({
        objectType,
        objectId,
        ctaUrl,
        appName,
        tab: collaborationSidebarState[TAB],
        enabledFeatures
      }),
      appName: appName,
      onMessage: handleOnMessage,
      className: isTabParentRendered ? 'hidden' : undefined
    })]
  });
}